import { Row, Col, Alert, Card, Statistic, Tooltip } from 'antd';
import { useSimulationReport } from '../../hooks/simulation/useSimulationReport';
import type {
  OtdBuckets,
  OtdReportCutKey,
  OtdReportCutRow,
  OtdReportData,
  SimulationDetail,
} from '../../store/simulation/types';

export interface SimulationOtdReportTabProps {
  simulation: SimulationDetail;
}

// v1 cuts render as risk-profile lists in the order below. v1.1 cuts (originState
// heatmap, destinationState heatmap, packageWeight bar) will be added as chart
// sections once we lock the visualization components.
const V1_CUTS: { key: OtdReportCutKey; title: string }[] = [
  { key: 'serviceLevel', title: 'By Service Level' },
  { key: 'carrier', title: 'By Carrier' },
  { key: 'serviceMethod', title: 'By Service Method' },
  { key: 'overTime', title: 'Over Time (Weekly)' },
];

// Ant Design semantic colors — match Statistic's default success/error tones
// so the risk-profile bars and the delta text share a palette.
const BUCKET_COLORS = {
  green: '#52c41a',
  yellow: '#faad14',
  red: '#ff4d4f',
};
const BUCKET_EMPTY_BG = '#f5f5f5';

// Backend already returns values in percentage form (e.g. 94.3 for 94.3%
// on OTD pcts, 1.2 for +1.2pp on delta) — render as-is, no additional scaling.
const formatPct = (v: number | undefined) => (v == null ? '—' : `${v.toFixed(1)}%`);
const formatDelta = (v: number | undefined) => {
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}`;
};

// Historical OTD comes from either the new backend field (`historicalOtdPct`)
// or the pre-rename legacy field (`actualOtdPct`) — whichever is present.
// Prefer historical; keep the fallback while the paired backend MR rolls out.
type WithHistoricalFallback = { historicalOtdPct?: number; actualOtdPct?: number };
const readHistorical = (r: WithHistoricalFallback): number | undefined => r.historicalOtdPct ?? r.actualOtdPct;

// -----------------------------------------------------------------------
// RiskProfileBar — horizontal stacked bar showing R/Y/G bucket distribution.
// Each segment's width is proportional to the bucket's shipment count.
// Segments are tooltipped with count + per-bucket historical OTD % (when
// full mode + bucket non-empty).
// -----------------------------------------------------------------------
interface RiskProfileBarProps {
  buckets?: OtdBuckets;
  height?: number;
}
const RiskProfileBar = ({ buckets, height = 22 }: RiskProfileBarProps) => {
  const g = buckets?.green.n ?? 0;
  const y = buckets?.yellow.n ?? 0;
  const r = buckets?.red.n ?? 0;
  const total = g + y + r;
  if (total === 0) {
    return <div style={{ height, background: BUCKET_EMPTY_BG, borderRadius: 4 }} />;
  }
  const pct = (n: number) => (n / total) * 100;
  const segmentTitle = (name: string, n: number, hist?: number): string => {
    const parts = [`${name}: ${n.toLocaleString()} shipment${n === 1 ? '' : 's'}`];
    if (hist !== undefined) parts.push(`Historical OTD: ${hist.toFixed(1)}%`);
    return parts.join(' · ');
  };
  return (
    <div
      style={{
        display: 'flex',
        height,
        borderRadius: 4,
        overflow: 'hidden',
        background: BUCKET_EMPTY_BG,
      }}
    >
      {g > 0 && (
        <Tooltip title={segmentTitle('Green', g, buckets!.green.historicalOtdPct)}>
          <div style={{ width: `${pct(g)}%`, background: BUCKET_COLORS.green }} />
        </Tooltip>
      )}
      {y > 0 && (
        <Tooltip title={segmentTitle('Yellow', y, buckets!.yellow.historicalOtdPct)}>
          <div style={{ width: `${pct(y)}%`, background: BUCKET_COLORS.yellow }} />
        </Tooltip>
      )}
      {r > 0 && (
        <Tooltip title={segmentTitle('Red', r, buckets!.red.historicalOtdPct)}>
          <div style={{ width: `${pct(r)}%`, background: BUCKET_COLORS.red }} />
        </Tooltip>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------
// RiskProfileRow — one row per cut item. Label + volume on top, the risk
// profile bar spanning the width, numeric means (Pred / Hist / Δ) to the
// right. Replaces the previous columnar Table treatment.
// -----------------------------------------------------------------------
interface RiskProfileRowProps {
  label: string;
  volume: number;
  predictedOtdPct: number;
  historicalOtdPct?: number;
  deltaPp?: number;
  buckets?: OtdBuckets;
  isFullMode: boolean;
}
const RiskProfileRow = ({
  label,
  volume,
  predictedOtdPct,
  historicalOtdPct,
  deltaPp,
  buckets,
  isFullMode,
}: RiskProfileRowProps) => (
  <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        marginBottom: 6,
        gap: 16,
        fontSize: 13,
      }}
    >
      <div>
        <b>{label}</b>
        <span style={{ color: '#8c8c8c', marginLeft: 8 }}>
          {volume.toLocaleString()} shipment{volume === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 20, fontVariantNumeric: 'tabular-nums' }}>
        <div>
          <span style={{ color: '#8c8c8c' }}>Pred </span>
          <b>{formatPct(predictedOtdPct)}</b>
        </div>
        {isFullMode && historicalOtdPct !== undefined && (
          <div>
            <span style={{ color: '#8c8c8c' }}>Hist </span>
            <b>{formatPct(historicalOtdPct)}</b>
          </div>
        )}
        {isFullMode && deltaPp !== undefined && (
          <div style={{ color: deltaPp >= 0 ? '#3f8600' : '#cf1322' }}>
            Δ <b>{formatDelta(deltaPp)}</b>
          </div>
        )}
      </div>
    </div>
    <RiskProfileBar buckets={buckets} />
  </div>
);

// -----------------------------------------------------------------------
// BucketLegend — persistent explanation of what the R/Y/G thresholds mean.
// Placed once near the top of the report so users can scan back to it.
// -----------------------------------------------------------------------
const BucketLegend = () => (
  <div style={{ display: 'flex', gap: 20, fontSize: 12, color: '#595959', alignItems: 'center' }}>
    <span>
      <span
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: 2,
          background: BUCKET_COLORS.green,
          marginRight: 6,
          verticalAlign: 'middle',
        }}
      />
      Green — predicted OTD ≥ 80%
    </span>
    <span>
      <span
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: 2,
          background: BUCKET_COLORS.yellow,
          marginRight: 6,
          verticalAlign: 'middle',
        }}
      />
      Yellow — 50–80%
    </span>
    <span>
      <span
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: 2,
          background: BUCKET_COLORS.red,
          marginRight: 6,
          verticalAlign: 'middle',
        }}
      />
      Red — &lt; 50%
    </span>
  </div>
);

export const SimulationOtdReportTab = ({ simulation }: SimulationOtdReportTabProps) => {
  const isComplete = simulation.simulationStatus === 'completed';
  const { reportData: rawReportData, loading } = useSimulationReport(simulation, 'otd-report');
  const reportData = rawReportData as OtdReportData | undefined;

  if (!isComplete) {
    return (
      <Alert
        type={'warning'}
        showIcon={true}
        message={'Simulation Incomplete'}
        description={'The OTD report will be available once the simulation has completed execution.'}
      />
    );
  }

  if (!reportData) {
    return (
      <Alert
        type={'info'}
        showIcon={true}
        message={loading ? 'Loading OTD report…' : 'OTD report not yet available'}
        description={
          loading
            ? undefined
            : 'The OTD report is generated after scoring completes. If the simulation just finalized, check back in a moment.'
        }
      />
    );
  }

  const { headline, cuts } = reportData;
  const isFullMode = headline.displayMode === 'full';
  const headlineHistorical = readHistorical(headline);

  // Empty-state: the sim was scored but the simulation-selected service method
  // matched the historical method for every shipment, so there's nothing to
  // compare. Distinct from "no report yet" — this IS the report, it's just
  // empty by nature of the sim's decisions.
  //
  // Loose check (!x rather than === 0): if the backend ever returns a
  // payload where nShipmentsEvaluated is missing or null (rollout window,
  // upstream change), treat it as an empty-state rather than tumbling into
  // the render below with undefined fields and crashing.
  if (!headline.nShipmentsEvaluated) {
    return (
      <Alert
        type={'info'}
        showIcon={true}
        message={'No shipment reroutes to report on'}
        description={
          'The simulation-selected service method matched the historical one for every shipment. ' +
          'The OTD report calculates predicted on-time delivery only for shipments where the ' +
          'simulation selected a different method.'
        }
      />
    );
  }

  const pctChanged = headline.nShipmentsTotal > 0 ? (headline.nShipmentsEvaluated / headline.nShipmentsTotal) * 100 : 0;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Alert
          type={'info'}
          showIcon={true}
          message={`Showing ${headline.nShipmentsEvaluated.toLocaleString()} of ${headline.nShipmentsTotal.toLocaleString()} shipments (${pctChanged.toFixed(1)}%) — those where the simulation-selected service method differs from the historical service method.`}
        />
      </Col>

      <Col span={24}>
        <Card>
          <Row gutter={16}>
            <Col flex={'1'}>
              <Statistic
                title={'Shipments (changed / total)'}
                value={`${headline.nShipmentsEvaluated.toLocaleString()} / ${headline.nShipmentsTotal.toLocaleString()}`}
              />
            </Col>
            <Col flex={'1'}>
              <Statistic
                title={'Predicted OTD'}
                value={headline.predictedOtdPct !== undefined ? headline.predictedOtdPct.toFixed(1) : '—'}
                suffix={headline.predictedOtdPct !== undefined ? '%' : undefined}
              />
            </Col>
            {isFullMode && headlineHistorical !== undefined && (
              <Col flex={'1'}>
                <Statistic title={'Historical OTD'} value={headlineHistorical.toFixed(1)} suffix={'%'} />
              </Col>
            )}
            {isFullMode && headline.deltaPp !== undefined && (
              <Col flex={'1'}>
                <Statistic
                  title={'Delta'}
                  value={headline.deltaPp.toFixed(1)}
                  suffix={'pp'}
                  valueStyle={{ color: headline.deltaPp >= 0 ? '#3f8600' : '#cf1322' }}
                />
              </Col>
            )}
          </Row>

          {headline.predictedBuckets && (
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  fontSize: 12,
                  color: '#8c8c8c',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  marginBottom: 8,
                }}
              >
                Overall risk profile
              </div>
              <RiskProfileBar buckets={headline.predictedBuckets} height={28} />
              <div style={{ marginTop: 10 }}>
                <BucketLegend />
              </div>
            </div>
          )}
        </Card>
      </Col>

      {V1_CUTS.map(({ key, title }) => {
        const rows = (cuts[key] ?? []) as OtdReportCutRow[];
        return (
          <Col span={24} key={key}>
            <Card title={title} loading={loading}>
              {rows.length === 0 ? (
                <div style={{ color: '#8c8c8c', fontSize: 13 }}>No shipments in this cut.</div>
              ) : (
                rows.map((row) => (
                  <RiskProfileRow
                    key={row.key}
                    label={row.label}
                    volume={row.volume}
                    predictedOtdPct={row.predictedOtdPct}
                    historicalOtdPct={readHistorical(row)}
                    deltaPp={row.deltaPp}
                    buckets={row.predictedBuckets}
                    isFullMode={isFullMode}
                  />
                ))
              )}
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};
